import type { either, remote, remoteOption } from '@code-expert/prelude';
import { task } from '@code-expert/prelude';
import type { QuestionId, QuizAttemptId, QuizSnapshotId } from '/imports/modules/Quizzes/domain';
import type { QuestionItem } from '/imports/modules/Quizzes/shared/presentation/domain';
import type { QuizApi } from '/imports/modules/Quizzes/shared/presentation/QuizApi';
import { quizApi } from '/imports/modules/Quizzes/shared/presentation/QuizApi';
import type {
  AttemptQuestion,
  UpdateAttemptError,
} from '/imports/modules/Quizzes/solve/shared/domain';
import { invokeProcedure, useCachedQuery } from '/imports/ui/hooks/useProcedure';
import type { PublicQuiz } from '../../domain/PublicQuiz';

export interface SolveQuizApi extends QuizApi {
  useQuizForAttempt: (attemptId: QuizAttemptId) => remoteOption.RemoteOption<PublicQuiz>;

  useAttemptQuestion: (
    attemptId: QuizAttemptId,
    questionId: QuestionId,
  ) => remoteOption.RemoteOption<AttemptQuestion>;

  useQuestionTitles: (attemptId: QuizAttemptId) => remote.Remote<Array<QuestionItem>>;

  updateAttempt: (
    attemptId: QuizAttemptId,
    questionId: QuestionId,
    snapshotId: QuizSnapshotId,
    blankValues: Array<string>,
  ) => Promise<either.Either<UpdateAttemptError, void>>;
}

export const solveQuizApi: SolveQuizApi = {
  ...quizApi,

  useQuizForAttempt: (attemptId) =>
    useCachedQuery('quizzes.findQuizForAttempt', { props: attemptId })[0],

  useAttemptQuestion: (attemptId, questionId) =>
    useCachedQuery('quizzes.findAttemptQuestion', { props: { attemptId, questionId } })[0],

  useQuestionTitles: (attemptId) =>
    useCachedQuery('quizzes.getQuestionTitles', { props: attemptId })[0],

  updateAttempt: (attemptId, questionId, snapshotId, blankValues) =>
    task.run(
      invokeProcedure('quizzes.updateAttempt')({ attemptId, questionId, snapshotId, blankValues }),
    ),
};
